import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Search, ImageOff, ExternalLink, Eye } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { useVendorProducts, useVendorRefresh } from "@/hooks/useVendor";
import { formatPrice } from "@/lib/format";
import { Pill, TableWrap, Td, Th, SelectCheckbox, BulkDeleteBar, inputCls } from "@/pages/admin/ui";

const STATUS_TONE = { published: "green", draft: "slate", pending: "amber", disabled: "rose" };
const STATUS_LABEL = { published: "Publié", draft: "Brouillon", pending: "En attente", disabled: "Désactivé" };

export default function VendorProducts() {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const { data: products, isLoading } = useVendorProducts({ q: search });
  const refresh = useVendorRefresh();
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const items = useMemo(() => products || [], [products]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = (ids) => setSelected((s) => (s.length >= ids.length ? [] : ids));

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer « ${name} » ?`)) return;
    try {
      await api.delete(`/vendor/products/${id}`);
      toast.success("Produit supprimé");
      refresh(["vendor-products", "vendor-dashboard"]);
    } catch (e) { toast.error(apiError(e)); }
  };

  const bulkDelete = async () => {
    if (!selected.length || !window.confirm(`Supprimer ${selected.length} produit(s) ?`)) return;
    setBusy(true);
    try {
      await api.post("/vendor/products/bulk-delete", { ids: selected });
      toast.success(`${selected.length} produit(s) supprimé(s)`);
      setSelected([]);
      refresh(["vendor-products", "vendor-dashboard"]);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const setStatus = async (p, status) => {
    try {
      const payload = { ...p };
      delete payload.id; delete payload.stock; delete payload.sales_count; delete payload.rating; delete payload.reviews_count;
      delete payload.created_at; delete payload.updated_at; delete payload.owner_id; delete payload.owner_type;
      delete payload.vendor_name; delete payload.vendor_slug;
      payload.status = status;
      await api.put(`/vendor/products/${p.id}`, payload);
      toast.success(status === "published" ? "Produit publié" : "Produit mis en brouillon");
      refresh(["vendor-products"]);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="vendor-products">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold text-ink">Mes produits</h1>
          <p className="mt-1 text-sm text-slate-500">{items.length} produit(s) · Gérez vos produits numériques, licences et services.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="relative">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input value={query} data-testid="vendor-product-search"
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && setSearch(query)}
              onBlur={() => setSearch(query)}
              placeholder="Rechercher..." className="h-10 w-44 rounded-lg border border-line pl-9 pr-3 text-sm focus:border-brand focus:outline-none" />
          </span>
          <Link to="/vendeur/produits/nouveau" data-testid="vendor-add-product"
            className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-dark">
            <Plus className="h-4 w-4" /> Ajouter un produit
          </Link>
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
      ) : !items.length ? (
        <div className="rounded-2xl border border-dashed border-line bg-white p-10 text-center">
          <p className="text-sm text-slate-500">Vous n'avez pas encore de produit.</p>
          <Link to="/vendeur/produits/nouveau" className="mt-4 inline-block rounded-full bg-brand px-6 py-3 text-sm font-bold text-white">+ Ajouter mon premier produit</Link>
        </div>
      ) : (
        <div className="rounded-2xl border border-line bg-white">
          <BulkDeleteBar count={selected.length} onDelete={bulkDelete} testId="vendor-bulk-bar" />
          <TableWrap>
            <table className="w-full min-w-[900px]">
              <thead>
                <tr className="border-b border-line text-left">
                  <Th className="w-9">
                    <SelectCheckbox testId="vendor-bulk-all"
                      checked={items.length > 0 && selected.length >= items.length}
                      onChange={() => toggleAll(items.map((p) => p.id))} />
                  </Th>
                  <Th>Produit</Th><Th>Catégorie</Th><Th>Prix</Th><Th>Stock</Th><Th>Statut</Th><Th className="text-right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-line last:border-0" data-testid={`vproduct-row-${p.id}`}>
                    <Td>
                      <SelectCheckbox testId={`vendor-bulk-${p.id}`} checked={selected.includes(p.id)} onChange={() => toggle(p.id)} />
                    </Td>
                    <Td>
                      <span className="flex items-center gap-3">
                        {p.image_url ? (
                          <img src={fileUrl(p.image_url)} alt="" className="h-10 w-10 rounded-lg border border-line bg-slate-50 object-contain p-1" />
                        ) : (
                          <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-line bg-slate-50"><ImageOff className="h-4 w-4 text-slate-300" /></span>
                        )}
                        <span className="min-w-0">
                          <span className="block max-w-[240px] truncate font-semibold">{p.name}</span>
                          <span className="block text-[11px] text-slate-400">/{p.slug}</span>
                        </span>
                      </span>
                    </Td>
                    <Td className="text-slate-500">{p.category_slug || "—"}</Td>
                    <Td>
                      <span className="font-bold">{formatPrice(p.price)}</span>
                      {p.old_price > p.price && <span className="ml-1.5 text-[11px] text-slate-400 line-through">{formatPrice(p.old_price)}</span>}
                    </Td>
                    <Td>
                      {p.delivery_mode === "license_key" && p.stock_managed !== false ? (
                        <Pill tone={p.stock > 5 ? "green" : p.stock > 0 ? "amber" : "rose"}>{p.stock} clé(s)</Pill>
                      ) : (
                        <Pill tone="slate">Sur demande</Pill>
                      )}
                    </Td>
                    <Td>
                      <span className="flex flex-wrap gap-1">
                        <Pill tone={STATUS_TONE[p.status] || "slate"}>{STATUS_LABEL[p.status] || p.status}</Pill>
                        {p.is_featured && <Pill tone="blue">Vedette</Pill>}
                      </span>
                    </Td>
                    <Td className="text-right">
                      <span className="flex justify-end gap-1.5">
                        {p.status === "published" && (
                          <a href={`/boutique/${p.slug}`} target="_blank" rel="noreferrer" title="Voir la fiche publique"
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand">
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        )}
                        <button onClick={() => setStatus(p, p.status === "published" ? "draft" : "published")}
                          data-testid={`vproduct-toggle-${p.id}`}
                          title={p.status === "published" ? "Dépublier" : "Publier"}
                          className={`flex h-8 w-8 items-center justify-center rounded-lg border ${p.status === "published" ? "border-amber-200 text-amber-500 hover:bg-amber-50" : "border-emerald-200 text-emerald-600 hover:bg-emerald-50"}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <Link to={`/vendeur/produits/${p.id}`} data-testid={`vproduct-edit-${p.id}`} title="Modifier"
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-brand hover:text-brand">
                          <Pencil className="h-3.5 w-3.5" />
                        </Link>
                        <button onClick={() => remove(p.id, p.name)} data-testid={`vproduct-delete-${p.id}`} title="Supprimer"
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-rose-500 hover:bg-rose-50">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        </div>
      )}
    </div>
  );
}
